import { LOCKED_ROOM_TYPES, resolveRoomTypeVocabId } from "./constants";
import type {
  InteriorDesignerState,
  PanelId,
  PhotoSuggestion,
  TreeNode,
} from "./types";
import { TARGET_PANEL_TO_PANEL_ID } from "./types";

/**
 * Returns which panel chips are available based on room condition
 * and progression flags.
 */
export const getAvailableChips = ({
  state,
}: {
  state: InteriorDesignerState;
}): PanelId[] => {
  const { classification, hasCompletedFinishing } = state;
  const condition = classification.room_condition;

  const chips: PanelId[] = ["interiorFinishing"];

  const isGoodCondition = condition === "good";
  const finishingUnlocksOthers =
    (condition === "shell" || condition === "dated" || condition === null) &&
    hasCompletedFinishing;

  if (isGoodCondition || finishingUnlocksOthers) {
    chips.push("furnishing", "wallColor");
  }

  chips.push("otherWishes");

  return chips;
};

export const isFurnitureToggleVisible = ({
  state,
}: {
  state: InteriorDesignerState;
}): boolean => {
  return (
    state.classification.furnished !== null || state.hasCompletedFurnishing
  );
};

/**
 * Returns whether the furniture toggle can be interacted with.
 * Disabled when the room is unfurnished and the user hasn't
 * completed a furnishing action yet.
 */
export const isFurnitureToggleEnabled = ({
  state,
}: {
  state: InteriorDesignerState;
}): boolean => {
  return (
    state.classification.furnished === true || state.hasCompletedFurnishing
  );
};

/**
 * Returns whether the room-type selector should be disabled.
 * Bathrooms and kitchens should not be reclassified by the user.
 */
export const isRoomTypeLocked = ({
  state,
}: {
  state: InteriorDesignerState;
}): boolean => {
  const vocabId = resolveRoomTypeVocabId({
    classificationRoomType: state.classification.room_type,
  });
  return vocabId !== null && LOCKED_ROOM_TYPES.has(vocabId);
};

export const isCtaEnabled = ({
  state,
  panelId,
}: {
  state: InteriorDesignerState;
  panelId: PanelId;
}): boolean => {
  const { panelSelections } = state;

  switch (panelId) {
    case "interiorFinishing":
      return (
        panelSelections.interiorFinishing.floor !== null ||
        panelSelections.interiorFinishing.wall !== null ||
        panelSelections.interiorFinishing.extra_wensen.trim().length > 0
      );
    case "furnishing":
      return (
        panelSelections.furnishing.style !== null ||
        panelSelections.furnishing.room_type.length > 0 ||
        panelSelections.furnishing.extra_wensen.trim().length > 0
      );
    case "wallColor":
      return (
        panelSelections.wallColor.ral_code !== null ||
        panelSelections.wallColor.custom_text.trim().length > 0 ||
        panelSelections.wallColor.extra_wensen.trim().length > 0
      );
    case "otherWishes":
      return panelSelections.otherWishes.text.trim().length > 0;
    case "history":
      return false;
    default:
      return false;
  }
};

/**
 * Returns non-deleted tree nodes sorted newest-first.
 * Optionally excludes a specific node by ID.
 */
export const getActiveNodesSorted = ({
  tree,
  excludeId,
}: {
  tree: TreeNode[];
  excludeId?: string;
}): TreeNode[] => {
  return tree
    .filter(
      (n) =>
        n.deleted_at === null &&
        (excludeId === undefined || n.id !== excludeId),
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
};

export const getActiveNode = ({
  state,
}: {
  state: InteriorDesignerState;
}): TreeNode | undefined => {
  return state.tree.find((n) => n.id === state.activeNodeId);
};

export const getActiveImageUrl = ({
  state,
}: {
  state: InteriorDesignerState;
}): string | null => {
  return state.activePhoto?.blob_url ?? null;
};

/**
 * Filters suggestions to those with a known target_panel mapping.
 */
export const getValidSuggestions = ({
  state,
}: {
  state: InteriorDesignerState;
}): PhotoSuggestion[] => {
  return state.suggestions.filter(
    (s) => s.target_panel in TARGET_PANEL_TO_PANEL_ID,
  );
};

export const hasSuggestions = ({
  state,
}: {
  state: InteriorDesignerState;
}): boolean => {
  return getValidSuggestions({ state }).length > 0;
};

/**
 * Returns whether the tree contains any generated (non-root) photos.
 */
export const hasHistory = ({
  state,
}: {
  state: InteriorDesignerState;
}): boolean => {
  return state.tree.some(
    (n) => n.id !== state.rootPhotoId && n.deleted_at === null,
  );
};

/**
 * Finds the newest non-deleted child of the active node
 * that has a remove_furniture action.
 */
export const findRemoveFurnitureChild = ({
  state,
}: {
  state: InteriorDesignerState;
}): TreeNode | null => {
  return (
    state.tree
      .filter(
        (n) =>
          n.parent_photo_id === state.activeNodeId &&
          n.deleted_at === null &&
          n.actions?.some((a) => a.type === "remove_furniture"),
      )
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )[0] ?? null
  );
};
